import Button, { VARIANT } from "@metacrm/metacrm-material-ui/dist/Button";
import InputField, { SIZE } from "@metacrm/metacrm-material-ui/dist/InputField";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import { Box } from "@mui/material";
import LoadingIcon from "components/LoadingComponent/LoadingIcon";
import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";
import useGetKnowledgeBase from "./useGetKnowledgeBase";
import EmptyImgStatus from "components/EmptyState/EmptyImageStatus";
import KnowledgeBaseModal from "./KnowledgeBaseModals/KnowledgeBaseModal";
import { map, set } from "lodash";
import { formatDate } from "features/helpers/utils";
import ActionButton from "./ActionButton";
import useGetPagePermission from "hooks/useGetPagePermission";

export default function KnowledgeBase() {
  const { formatMessage } = useIntl();
  const { data, headCells, handleRefresh, fetchData, isLoading, cellWidth } =
    useGetKnowledgeBase();
  const [openModal, setOpenModal] = useState(false);
  function handleOpenModal() {
    setOpenModal(true);
  }

  const renderContents = (knowledge) => {
    const renderStatus = (status) => {
      switch (status) {
        case "completed":
          return (
            <Box color="#222">
              {formatMessage({ id: "global.status.synced" })}
            </Box>
          );
        case "pending":
          return (
            <Box color="#7B61FF">
              {formatMessage({ id: "global.status.syncing" })}
            </Box>
          );
        case "failed":
          return (
            <Box color="#FC5555">
              {formatMessage({ id: "global.status.failed" })}
            </Box>
          );
        default:
          return (
            <Box color="#222">
              {formatMessage({ id: "global.status.synced" })}
            </Box>
          );
      }
    };

    const isPending = knowledge.status === "pending";

    const contents = [
      <Box slot="name" className="truncate" sx={{ maxWidth: cellWidth }}>
        {knowledge.name}
      </Box>,
      <Box slot="type">{knowledge.sourceType}</Box>,
      <Box slot="createdAt">{formatDate(knowledge.createdAt)}</Box>,
      <Box slot="status">{renderStatus(knowledge.status)}</Box>,
      <Box slot="action">
        <ActionButton
          knowledge={knowledge}
          length={data?.length}
          refetch={fetchData}
          disabled={isPending || readonly}
          refreshBtn={knowledge.sourceType === "Macro" && isPending}
        />
      </Box>,
    ];

    return contents;
  };

  const isSyncing = data.some((item) => item.status === "pending");
  const pagePermission = useGetPagePermission();
  const { readonly } = pagePermission("aiManagement");

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: "24px",
        }}
      >
        <div>
          <div className="title">
            {formatMessage({ id: "global.function.knowledgeBase" })}
          </div>
          <div className="description">
            {formatMessage({ id: "global.function.knowledgeBase.description" })}
          </div>
        </div>
        <Button
          onClick={handleOpenModal}
          size={SIZE.MEDIUM}
          sx={{ fontSize: "12px" }}
          disabled={data.length >= 10 || readonly}
        >
          {formatMessage({ id: "popup.createKnowledge.title" })}
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          mb: "8px",
        }}
      >
        <Button
          variant={"text"}
          size={SIZE.MEDIUM}
          sx={{ padding: "16px" }}
          onClick={isSyncing ? () => {} : handleRefresh}
          disabled={isSyncing || data.length === 0 || readonly}
        >
          <Box
            className="item-flex"
            sx={{ fontSize: 12, fontWeight: 700, gap: "0 4px" }}
          >
            <i className="meta-crm-icon-ic_refresh_up font-size-12" />
            {formatMessage({ id: "global.btn.syncKnowledge" })}
          </Box>
        </Button>

        <Box color="#555" fontSize={12} fontWeight={400}>
          {data.length || 0} of {data.length || 0} record(s)
        </Box>
      </Box>

      <CRMTable
        containerStyle={{
          // maxHeight: `calc(100vh - 160px)`,
          // height: "calc(100vh - 160px)",
          marginTop: "8px",
        }}
        rowData={data}
        headers={headCells}
        contents={map(data, (user, i) => renderContents(user))}
        isLoading={isLoading}
        LoadingContent={<LoadingIcon />}
        noDataComponent={
          <Box height="calc(100vh - 240px)">
            <EmptyImgStatus
              type="campaign"
              title={formatMessage({ id: "emptyStatus.knowledgeBase.title" })}
              description={formatMessage({
                id: "emptyStatus.knowledgeBase.description",
              })}
              onClick={handleOpenModal}
              buttonText={formatMessage({ id: "global.btn.addKnowledge" })}
            />
          </Box>
        }
      />

      <KnowledgeBaseModal
        refetch={fetchData}
        open={openModal}
        onClose={() => setOpenModal(false)}
        knowledgeBaseData={data}
      />
    </div>
  );
}
